import React, { useState, useEffect } from 'react';
import { Upload, FileText, Users, Clock, StickyNote, CheckCircle, XCircle, AlertCircle, RefreshCw, Zap, Loader2, FolderOpen } from 'lucide-react';

const Import = () => {
  const [activeTab, setActiveTab] = useState('api-sync');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState(null);
  const [forceUpdate, setForceUpdate] = useState(false);

  // Clio API Sync state
  const [clioStatus, setClioStatus] = useState(null);
  const [syncPreview, setSyncPreview] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResults, setSyncResults] = useState(null);
  const [syncOptions, setSyncOptions] = useState({
    force_update: false,
    sync_matters: true,
    sync_contacts: true,
    sync_time_entries: true,
    sync_communications: true,
    sync_documents: false  // Off by default since documents can be large
  });

  const importTypes = [
    { id: 'api-sync', name: 'Clio API Sync', icon: Zap, endpoint: null },
    { id: 'cases', name: 'CSV: Cases', icon: FileText, endpoint: '/api/import/cases' },
    { id: 'contacts', name: 'CSV: Contacts', icon: Users, endpoint: '/api/import/contacts' },
    { id: 'time', name: 'CSV: Time', icon: Clock, endpoint: '/api/import/time-entries' },
    { id: 'notes', name: 'CSV: Updates', icon: StickyNote, endpoint: '/api/import/case-updates' }
  ];

  // Load Clio status on mount
  useEffect(() => {
    if (activeTab === 'api-sync') {
      loadClioStatus();
      loadSyncPreview();
    }
  }, [activeTab]);

  const loadClioStatus = async () => {
    try {
      const response = await fetch('/api/clio/status', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setClioStatus(data);
      }
    } catch (error) {
      console.error('Error loading Clio status:', error);
    }
  };

  const loadSyncPreview = async () => {
    try {
      const response = await fetch('/api/clio/preview', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSyncPreview(data);
      }
    } catch (error) {
      console.error('Error loading sync preview:', error);
    }
  };

  const startClioSync = async () => {
    setSyncing(true);
    setSyncResults(null);

    try {
      const response = await fetch('/api/clio/sync/now', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(syncOptions)
      });

      const data = await response.json();
      if (response.ok) {
        setSyncResults(data.results);
        loadSyncPreview(); // Refresh preview
      } else {
        setSyncResults({ error: data.detail });
      }
    } catch (error) {
      setSyncResults({ error: error.message });
    } finally {
      setSyncing(false);
    }
  };

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setResults(null);

    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const response = await fetch('/api/import/preview', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setPreview(data);
      }
    } catch (error) {
      console.error('Error previewing file:', error);
    }
  };

  const handleImport = async () => {
    if (!file) return;

    setImporting(true);
    setResults(null);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('force_update', forceUpdate.toString());

    const currentType = importTypes.find(t => t.id === activeTab);

    try {
      const response = await fetch(currentType.endpoint, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setResults(data);
        setFile(null);
        setPreview(null);
      } else {
        const error = await response.json();
        setResults({ error: error.detail });
      }
    } catch (error) {
      setResults({ error: error.message });
    } finally {
      setImporting(false);
    }
  };

  const renderClioSync = () => (
    <div className="space-y-6">
      {/* Connection Status */}
      <div className="bg-slate-800 rounded-lg border border-slate-700 p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2 text-white">
          <Zap className="w-5 h-5 text-cyan-400" />
          Clio API Connection
        </h3>

        {clioStatus ? (
          <div className={`flex items-center gap-2 p-3 rounded-lg ${
            clioStatus.connected ? 'bg-green-900/30 text-green-400 border border-green-700' : 'bg-red-900/30 text-red-400 border border-red-700'
          }`}>
            {clioStatus.connected ? (
              <CheckCircle className="w-5 h-5" />
            ) : (
              <XCircle className="w-5 h-5" />
            )}
            <span>{clioStatus.message}</span>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin" />
            Checking connection...
          </div>
        )}
      </div>

      {/* Sync Preview */}
      {syncPreview && clioStatus?.connected && (
        <div className="bg-slate-800 rounded-lg border border-slate-700 p-6">
          <h3 className="text-lg font-semibold mb-4 text-white">Sync Preview</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
              <p className="text-sm text-slate-400">Clio Matters</p>
              <p className="text-2xl font-bold text-white">{syncPreview.clio?.matters || 0}</p>
              <p className="text-xs text-slate-500">Synced: {syncPreview.virgo_synced?.matters || 0}</p>
            </div>
            <div className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
              <p className="text-sm text-slate-400">Clio Contacts</p>
              <p className="text-2xl font-bold text-white">{syncPreview.clio?.contacts || 0}</p>
              <p className="text-xs text-slate-500">Synced: {syncPreview.virgo_synced?.contacts || 0}</p>
            </div>
            <div className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
              <p className="text-sm text-slate-400">Clio Activities</p>
              <p className="text-2xl font-bold text-white">{syncPreview.clio?.activities || 0}</p>
              <p className="text-xs text-slate-500">Synced: {syncPreview.virgo_synced?.activities || 0}</p>
            </div>
            <div className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
              <p className="text-sm text-slate-400">Clio Tasks</p>
              <p className="text-2xl font-bold text-white">{syncPreview.clio?.tasks || 0}</p>
            </div>
            <div className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
              <p className="text-sm text-slate-400">Clio Documents</p>
              <p className="text-2xl font-bold text-white">{syncPreview.clio?.documents || 0}</p>
              <p className="text-xs text-slate-500">Synced: {syncPreview.virgo_synced?.documents || 0}</p>
            </div>
          </div>
        </div>
      )}

      {/* Sync Options */}
      {clioStatus?.connected && (
        <div className="bg-slate-800 rounded-lg border border-slate-700 p-6">
          <h3 className="text-lg font-semibold mb-4 text-white">Sync Options</h3>
          <div className="space-y-3">
            <label className="flex items-center gap-3 text-slate-200 cursor-pointer">
              <input
                type="checkbox"
                checked={syncOptions.sync_contacts}
                onChange={(e) => setSyncOptions({...syncOptions, sync_contacts: e.target.checked})}
                className="w-4 h-4 text-cyan-600 rounded bg-slate-700 border-slate-600"
              />
              <span>Sync Contacts</span>
            </label>
            <label className="flex items-center gap-3 text-slate-200 cursor-pointer">
              <input
                type="checkbox"
                checked={syncOptions.sync_matters}
                onChange={(e) => setSyncOptions({...syncOptions, sync_matters: e.target.checked})}
                className="w-4 h-4 text-cyan-600 rounded bg-slate-700 border-slate-600"
              />
              <span>Sync Cases (Matters)</span>
            </label>
            <label className="flex items-center gap-3 text-slate-200 cursor-pointer">
              <input
                type="checkbox"
                checked={syncOptions.sync_time_entries}
                onChange={(e) => setSyncOptions({...syncOptions, sync_time_entries: e.target.checked})}
                className="w-4 h-4 text-cyan-600 rounded bg-slate-700 border-slate-600"
              />
              <span>Sync Time Entries</span>
            </label>
            <label className="flex items-center gap-3 text-slate-200 cursor-pointer">
              <input
                type="checkbox"
                checked={syncOptions.sync_communications}
                onChange={(e) => setSyncOptions({...syncOptions, sync_communications: e.target.checked})}
                className="w-4 h-4 text-cyan-600 rounded bg-slate-700 border-slate-600"
              />
              <span>Sync Communications (Case Updates)</span>
            </label>
            <div className="border-t border-slate-700 pt-3 mt-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={syncOptions.sync_documents}
                  onChange={(e) => setSyncOptions({...syncOptions, sync_documents: e.target.checked})}
                  className="w-4 h-4 text-cyan-600 rounded bg-slate-700 border-slate-600"
                />
                <span className="text-slate-200 font-medium flex items-center gap-2">
                  <FolderOpen className="w-4 h-4 text-cyan-400" />
                  Sync Documents (Download Files)
                </span>
              </label>
              <p className="text-xs text-slate-500 ml-7 mt-1">
                Downloads all documents from Clio and stores them in VIRGO. This may take a while for large files.
              </p>
            </div>
            <div className="border-t border-slate-700 pt-3 mt-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={syncOptions.force_update}
                  onChange={(e) => setSyncOptions({...syncOptions, force_update: e.target.checked})}
                  className="w-4 h-4 text-orange-600 rounded bg-slate-700 border-slate-600"
                />
                <span className="text-orange-400 font-medium">Force Update (overwrite existing records)</span>
              </label>
              <p className="text-xs text-slate-500 ml-7 mt-1">
                When enabled, existing records will be updated with Clio data. Otherwise, duplicates are skipped.
              </p>
            </div>
          </div>

          <button
            onClick={startClioSync}
            disabled={syncing || !clioStatus?.connected}
            className="mt-6 w-full py-3 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:bg-slate-600 disabled:text-slate-400 flex items-center justify-center gap-2 transition-colors"
          >
            {syncing ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Syncing from Clio...
              </>
            ) : (
              <>
                <RefreshCw className="w-5 h-5" />
                Start Clio Sync
              </>
            )}
          </button>
        </div>
      )}

      {/* Sync Results */}
      {syncResults && (
        <div className="bg-slate-800 rounded-lg border border-slate-700 p-6">
          <h3 className="text-lg font-semibold mb-4 text-white">Sync Results</h3>

          {syncResults.error ? (
            <div className="bg-red-900/30 border border-red-700 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <XCircle className="w-5 h-5 text-red-400" />
                <span className="text-red-400 font-medium">Sync Failed</span>
              </div>
              <p className="text-red-300 mt-2">{syncResults.error}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {['matters', 'contacts', 'time_entries', 'communications', 'documents'].map((type) => {
                const result = syncResults[type];
                if (!result) return null;
                const total = (result.created || 0) + (result.updated || 0) + (result.skipped || 0);
                if (total === 0) return null;

                return (
                  <div key={type} className="border border-slate-700 rounded-lg p-4 bg-slate-700/30">
                    <h4 className="font-medium capitalize mb-2 text-white">{type.replace('_', ' ')}</h4>
                    <div className="grid grid-cols-3 gap-2 text-sm">
                      <div className="bg-green-900/30 border border-green-700 rounded p-2 text-center">
                        <p className="text-green-400 font-bold">{result.created || 0}</p>
                        <p className="text-green-500 text-xs">Created</p>
                      </div>
                      <div className="bg-blue-900/30 border border-blue-700 rounded p-2 text-center">
                        <p className="text-blue-400 font-bold">{result.updated || 0}</p>
                        <p className="text-blue-500 text-xs">Updated</p>
                      </div>
                      <div className="bg-slate-700/50 border border-slate-600 rounded p-2 text-center">
                        <p className="text-slate-300 font-bold">{result.skipped || 0}</p>
                        <p className="text-slate-400 text-xs">Skipped</p>
                      </div>
                    </div>
                    {type === 'documents' && result.downloaded_bytes > 0 && (
                      <div className="mt-2 text-xs text-cyan-400">
                        Downloaded {(result.downloaded_bytes / 1024 / 1024).toFixed(2)} MB
                      </div>
                    )}
                    {result.errors?.length > 0 && (
                      <div className="mt-2 text-xs text-red-400">
                        {result.errors.length} errors occurred
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Instructions */}
      <div className="bg-slate-900/20 border border-cyan-700 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-cyan-300 mb-2">How Clio API Sync Works</h3>
        <ul className="text-sm text-cyan-200 space-y-2 list-disc list-inside">
          <li>Connects directly to your Clio account via API</li>
          <li>Pulls all matters, contacts, time entries, and communications</li>
          <li><strong>Document Sync:</strong> Enable to download all files from Clio and store in VIRGO</li>
          <li>Automatically detects duplicates using Clio IDs - <strong>no duplicate data</strong></li>
          <li>Safe to run multiple times - only new data will be imported</li>
          <li>Use "Force Update" to overwrite existing records with Clio data</li>
        </ul>
      </div>
    </div>
  );

  const renderCSVImport = () => (
    <>
      {/* Upload Area */}
      <div className="bg-slate-800 rounded-lg border border-slate-700 p-6 mb-6">
        <div className="border-2 border-dashed border-slate-600 rounded-lg p-8">
          <div className="text-center">
            <Upload className="mx-auto h-12 w-12 text-slate-500" />
            <div className="mt-4">
              <label htmlFor="file-upload" className="cursor-pointer">
                <span className="mt-2 block text-sm font-medium text-white">
                  Upload CSV file
                </span>
                <span className="mt-1 block text-sm text-slate-400">
                  or drag and drop
                </span>
                <input
                  id="file-upload"
                  name="file-upload"
                  type="file"
                  accept=".csv"
                  className="sr-only"
                  onChange={handleFileSelect}
                />
              </label>
            </div>
            <p className="mt-2 text-xs text-slate-500">CSV files only</p>
          </div>
        </div>

        {file && (
          <div className="mt-4 bg-slate-700/50 p-4 rounded-lg border border-slate-600">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-sm font-medium text-white">{file.name}</p>
                  <p className="text-xs text-slate-400">{(file.size / 1024).toFixed(2)} KB</p>
                </div>
              </div>
              <button
                onClick={handleImport}
                disabled={importing}
                className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:bg-slate-600"
              >
                {importing ? 'Importing...' : 'Import'}
              </button>
            </div>

            {activeTab === 'cases' && (
              <div className="mt-3 pt-3 border-t border-slate-600">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={forceUpdate}
                    onChange={(e) => setForceUpdate(e.target.checked)}
                    className="w-4 h-4 text-cyan-600 bg-slate-700 border-slate-600 rounded"
                  />
                  <span className="text-sm font-medium text-slate-200">Force Update Existing Cases</span>
                </label>
                <p className="text-xs text-slate-500 mt-1 ml-6">
                  When enabled, existing cases with matching Clio IDs will be overwritten
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Preview */}
      {preview && !preview.error && (
        <div className="bg-slate-800 rounded-lg border border-slate-700 p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4 text-white">Preview</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-700">
              <thead className="bg-slate-700">
                <tr>
                  {preview.headers?.map((header, i) => (
                    <th key={i} className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-slate-800 divide-y divide-slate-700">
                {preview.sample_rows?.map((row, i) => (
                  <tr key={i}>
                    {preview.headers?.map((header, j) => (
                      <td key={j} className="px-6 py-4 whitespace-nowrap text-sm text-slate-200">
                        {row[header]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-sm text-slate-400 mt-4">
            Showing {preview.sample_rows?.length} of {preview.total_rows} rows
          </p>
        </div>
      )}

      {/* Results */}
      {results && (
        <div className="bg-slate-800 rounded-lg border border-slate-700 p-6">
          <h3 className="text-lg font-semibold mb-4 text-white">Import Results</h3>

          {results.error ? (
            <div className="bg-red-900/30 border border-red-700 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <XCircle className="w-5 h-5 text-red-400" />
                <span className="text-red-400 font-medium">Import Failed</span>
              </div>
              <p className="text-red-300 mt-2">{results.error}</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-green-900/30 border border-green-700 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    <span className="text-green-400 font-medium">Created</span>
                  </div>
                  <p className="text-2xl font-bold text-green-300">{results.success}</p>
                </div>

                {results.updated > 0 && (
                  <div className="bg-blue-900/30 border border-blue-700 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <CheckCircle className="w-5 h-5 text-blue-400" />
                      <span className="text-blue-400 font-medium">Updated</span>
                    </div>
                    <p className="text-2xl font-bold text-blue-300">{results.updated}</p>
                  </div>
                )}

                {results.failed > 0 && (
                  <div className="bg-red-900/30 border border-red-700 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <XCircle className="w-5 h-5 text-red-400" />
                      <span className="text-red-400 font-medium">Failed</span>
                    </div>
                    <p className="text-2xl font-bold text-red-300">{results.failed}</p>
                  </div>
                )}

                {results.orphaned > 0 && (
                  <div className="bg-yellow-900/30 border border-yellow-700 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <AlertCircle className="w-5 h-5 text-yellow-400" />
                      <span className="text-yellow-400 font-medium">Orphaned</span>
                    </div>
                    <p className="text-2xl font-bold text-yellow-300">{results.orphaned}</p>
                  </div>
                )}

                {results.duplicates > 0 && (
                  <div className="bg-yellow-900/30 border border-yellow-700 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <AlertCircle className="w-5 h-5 text-yellow-400" />
                      <span className="text-yellow-400 font-medium">Duplicates</span>
                    </div>
                    <p className="text-2xl font-bold text-yellow-300">{results.duplicates}</p>
                  </div>
                )}
              </div>

              {results.errors?.length > 0 && (
                <div className="bg-yellow-900/30 border border-yellow-700 rounded-lg p-4">
                  <h4 className="font-medium text-yellow-400 mb-2">Errors & Warnings</h4>
                  <ul className="text-sm text-yellow-300 space-y-1 max-h-60 overflow-y-auto">
                    {results.errors.map((error, i) => (
                      <li key={i}>• {error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      <div className="max-w-6xl mx-auto p-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-white">Import from Clio</h1>
          <p className="text-slate-400 mt-2">
            Sync data directly from Clio API or import from CSV exports
          </p>
        </div>

        {/* Import Type Tabs */}
        <div className="border-b border-slate-700 mb-6">
          <nav className="flex space-x-4 overflow-x-auto">
            {importTypes.map((type) => {
              const Icon = type.icon;
              return (
                <button
                  key={type.id}
                  onClick={() => {
                    setActiveTab(type.id);
                    setFile(null);
                    setPreview(null);
                    setResults(null);
                    setSyncResults(null);
                  }}
                  className={`py-4 px-3 border-b-2 font-medium text-sm flex items-center gap-2 whitespace-nowrap transition-colors ${
                    activeTab === type.id
                      ? 'border-cyan-500 text-cyan-400'
                      : 'border-transparent text-slate-400 hover:text-slate-200 hover:border-slate-600'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {type.name}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Content */}
        {activeTab === 'api-sync' ? renderClioSync() : renderCSVImport()}
      </div>
    </div>
  );
};

export default Import;
